import { DataSyncMetricsBucket } from '../../../types/DataSyncMetrics';
import {
  BucketPoint,
  buildBucketPointsForRange,
  buildBucketSeriesForRange,
  MetricsGranularity,
} from '../../../lib/metricsUtils';
import { buildPeriodChange, formatInteger, toNumber } from './analyticsMath';

export type StageCardKey =
  | 'received'
  | 'delivered'
  | 'ingest'
  | 'load'
  | 'mapping'
  | 'delivery';

export interface StageCardModel {
  key: StageCardKey;
  name: string;
  series: number[];
  trend: {
    delta: number;
    deltaPercent: number;
  };
  value: number;
}

interface BuildAnalyticsViewModelInput {
  buckets: DataSyncMetricsBucket[];
  previousBuckets: DataSyncMetricsBucket[];
  selectedStageKey: StageCardKey;
  fromEpoch: number;
  toEpoch: number;
  granularity: MetricsGranularity;
}

interface BuildAnalyticsViewModelOutput {
  cards: StageCardModel[];
  totalReceived: number;
  totalDelivered: number;
  receivedTrend: { delta: number; deltaPercent: number };
  deliveredTrend: { delta: number; deltaPercent: number };
  receivedSeries: number[];
  deliveredSeries: number[];
  detailSeries: BucketPoint[];
  detailTitle: string;
  detailDescription: string;
  detailValueLabel: string;
  detailPrimaryValue: number;
  detailSecondaryLine: string;
}

const filterStageBuckets = (
  buckets: DataSyncMetricsBucket[],
  stage: 'ingest' | 'load' | 'map' | 'export',
) => buckets.filter((bucket) => bucket.stage === stage);

export const buildAnalyticsViewModel = ({
  buckets,
  previousBuckets,
  selectedStageKey,
  fromEpoch,
  toEpoch,
  granularity,
}: BuildAnalyticsViewModelInput): BuildAnalyticsViewModelOutput => {
  const ingestBuckets = filterStageBuckets(buckets, 'ingest');
  const loadBuckets = filterStageBuckets(buckets, 'load');
  const mappingBuckets = filterStageBuckets(buckets, 'map');
  const exportBuckets = filterStageBuckets(buckets, 'export');

  const previousIngestBuckets = filterStageBuckets(previousBuckets, 'ingest');
  const previousLoadBuckets = filterStageBuckets(previousBuckets, 'load');
  const previousMappingBuckets = filterStageBuckets(previousBuckets, 'map');
  const previousExportBuckets = filterStageBuckets(previousBuckets, 'export');

  const ingestSuccessSeries = buildBucketSeriesForRange(
    ingestBuckets,
    (bucket) => toNumber(bucket.total_success_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const exportSuccessSeries = buildBucketSeriesForRange(
    exportBuckets,
    (bucket) => toNumber(bucket.total_success_count),
    fromEpoch,
    toEpoch,
    granularity,
  );

  const ingestErrorSeries = buildBucketSeriesForRange(
    ingestBuckets,
    (bucket) => toNumber(bucket.total_error_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const loadErrorSeries = buildBucketSeriesForRange(
    loadBuckets,
    (bucket) => toNumber(bucket.total_error_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const mappingErrorSeries = buildBucketSeriesForRange(
    mappingBuckets,
    (bucket) => toNumber(bucket.total_error_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const deliveryErrorSeries = buildBucketSeriesForRange(
    exportBuckets,
    (bucket) => toNumber(bucket.total_error_count),
    fromEpoch,
    toEpoch,
    granularity,
  );

  const totalReceived = ingestSuccessSeries.reduce(
    (sum, value) => sum + value,
    0,
  );
  const totalDelivered = exportSuccessSeries.reduce(
    (sum, value) => sum + value,
    0,
  );

  const previousTotalReceived = previousIngestBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_success_count),
    0,
  );
  const previousTotalDelivered = previousExportBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_success_count),
    0,
  );

  const totalFailedOnIngest = ingestErrorSeries.reduce(
    (sum, value) => sum + value,
    0,
  );
  const totalFailedOnLoad = loadErrorSeries.reduce(
    (sum, value) => sum + value,
    0,
  );
  const totalFailedOnMapping = mappingErrorSeries.reduce(
    (sum, value) => sum + value,
    0,
  );
  const totalFailedOnDelivery = deliveryErrorSeries.reduce(
    (sum, value) => sum + value,
    0,
  );

  const receivedBaseTotal = totalReceived + totalFailedOnIngest;
  const receivedSharePercent =
    receivedBaseTotal > 0 ? (totalReceived / receivedBaseTotal) * 100 : 0;
  const deliveredOfReceivedPercent =
    totalReceived > 0 ? (totalDelivered / totalReceived) * 100 : 0;

  const previousFailedOnIngest = previousIngestBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );
  const previousFailedOnLoad = previousLoadBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );
  const previousFailedOnMapping = previousMappingBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );
  const previousFailedOnDelivery = previousExportBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );

  const receivedTrend = buildPeriodChange(totalReceived, previousTotalReceived);
  const deliveredTrend = buildPeriodChange(
    totalDelivered,
    previousTotalDelivered,
  );
  const ingestErrorTrend = buildPeriodChange(
    totalFailedOnIngest,
    previousFailedOnIngest,
  );
  const loadErrorTrend = buildPeriodChange(
    totalFailedOnLoad,
    previousFailedOnLoad,
  );
  const mappingErrorTrend = buildPeriodChange(
    totalFailedOnMapping,
    previousFailedOnMapping,
  );
  const deliveryErrorTrend = buildPeriodChange(
    totalFailedOnDelivery,
    previousFailedOnDelivery,
  );

  const cards: StageCardModel[] = [
    {
      key: 'received',
      name: 'Events received',
      series: ingestSuccessSeries,
      trend: receivedTrend,
      value: totalReceived,
    },
    {
      key: 'delivered',
      name: 'Events delivered',
      series: exportSuccessSeries,
      trend: deliveredTrend,
      value: totalDelivered,
    },
    {
      key: 'ingest',
      name: 'Ingest',
      series: ingestErrorSeries,
      trend: ingestErrorTrend,
      value: totalFailedOnIngest,
    },
    {
      key: 'load',
      name: 'Load',
      series: loadErrorSeries,
      trend: loadErrorTrend,
      value: totalFailedOnLoad,
    },
    {
      key: 'mapping',
      name: 'Mapping',
      series: mappingErrorSeries,
      trend: mappingErrorTrend,
      value: totalFailedOnMapping,
    },
    {
      key: 'delivery',
      name: 'Delivery',
      series: deliveryErrorSeries,
      trend: deliveryErrorTrend,
      value: totalFailedOnDelivery,
    },
  ];

  const selectedStage =
    cards.find((stage) => stage.key === selectedStageKey) || cards[0];

  const detailTitle =
    selectedStage.key === 'received'
      ? 'Successfully received'
      : selectedStage.key === 'delivered'
        ? 'Successfully delivered'
        : `${selectedStage.name} stage`;
  const detailDescription =
    selectedStage.key === 'received'
      ? 'Events successfully received from source'
      : selectedStage.key === 'delivered'
        ? 'Events successfully delivered to destination'
        : `Performance for ${selectedStage.name.toLowerCase()} over the selected date range`;
  const detailValueLabel =
    selectedStage.key === 'received' || selectedStage.key === 'delivered'
      ? selectedStage.key === 'received'
        ? 'events received'
        : 'events delivered'
      : 'events failed';
  const detailPrimaryValue = selectedStage.value;
  const detailSecondaryLine =
    selectedStage.key === 'received' || selectedStage.key === 'delivered'
      ? selectedStage.key === 'received'
        ? `${receivedSharePercent.toFixed(0)}% of total received`
        : `${deliveredOfReceivedPercent.toFixed(0)}% delivered of successfully received`
      : `Change: ${formatInteger(selectedStage.trend.delta)} (${selectedStage.trend.deltaPercent.toFixed(1)}%)`;
  const selectedStageBuckets =
    selectedStage.key === 'received'
      ? ingestBuckets
      : selectedStage.key === 'delivered'
        ? exportBuckets
        : selectedStage.key === 'ingest'
          ? ingestBuckets
          : selectedStage.key === 'load'
            ? loadBuckets
            : selectedStage.key === 'mapping'
              ? mappingBuckets
              : exportBuckets;

  const detailValueSelector = (bucket: DataSyncMetricsBucket) =>
    selectedStage.key === 'received' || selectedStage.key === 'delivered'
      ? toNumber(bucket.total_success_count)
      : toNumber(bucket.total_error_count);

  const detailSeries = buildBucketPointsForRange(
    selectedStageBuckets,
    detailValueSelector,
    fromEpoch,
    toEpoch,
    granularity,
  );

  return {
    cards,
    totalReceived,
    totalDelivered,
    receivedTrend,
    deliveredTrend,
    receivedSeries: ingestSuccessSeries,
    deliveredSeries: exportSuccessSeries,
    detailSeries,
    detailTitle,
    detailDescription,
    detailValueLabel,
    detailPrimaryValue,
    detailSecondaryLine,
  };
};
